import { useEffect, useRef, useState } from "react";
import { CalendarPlus, Check } from "lucide-react";
import { googleCalUrl, downloadIcs } from "@/lib/calendar";

const GoogleGlyph = (props) => (
  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden {...props}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z" />
  </svg>
);

const AppleGlyph = (props) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden {...props}>
    <path d="M16.36 12.9c.02 2.5 2.19 3.33 2.22 3.34-.02.06-.35 1.2-1.15 2.37-.69 1.02-1.41 2.03-2.54 2.05-1.11.02-1.47-.66-2.74-.66-1.27 0-1.66.64-2.72.68-1.09.04-1.92-1.1-2.62-2.11-1.42-2.07-2.51-5.85-1.05-8.41.72-1.27 2.02-2.08 3.42-2.1 1.07-.02 2.09.72 2.74.72.66 0 1.89-.89 3.19-.76.54.02 2.07.22 3.05 1.65-.08.05-1.82 1.07-1.8 3Zm-2.1-5.86c.58-.7.97-1.68.86-2.65-.83.03-1.85.55-2.45 1.25-.53.62-1 1.61-.88 2.56.93.07 1.88-.47 2.47-1.16Z" />
  </svg>
);

export const AddToCalendar = ({ event, variant = "ghost", size = "", label = "Add to calendar", className = "", testId = "add-to-calendar", dark = false }) => {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const google = () => { window.open(googleCalUrl(event), "_blank", "noopener,noreferrer"); flash(); };
  const apple = () => { downloadIcs(event); flash(); };
  const flash = () => { setSaved(true); setOpen(false); setTimeout(() => setSaved(false), 2200); };

  const menuTone = dark
    ? "bg-[var(--navy-2)] border-white/12 text-[rgba(244,241,234,.9)]"
    : "bg-white border-[rgba(17,25,27,.12)] text-[var(--ink)]";
  const itemHover = dark ? "hover:bg-white/5" : "hover:bg-[rgba(212,168,77,.1)]";

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`btn btn-${variant} ${size} ${className}`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId}
      >
        {saved ? <Check size={16} /> : <CalendarPlus size={16} />} {saved ? "Added" : label}
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute z-50 top-full left-0 mt-2 min-w-[220px] rounded-2xl border p-1.5 shadow-[0_24px_60px_rgba(7,20,22,.28)] backdrop-blur-xl ${menuTone}`}
          data-testid={`${testId}-menu`}
        >
          <button type="button" onClick={google} className={`w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-left text-[13px] font-semibold transition-colors ${itemHover}`} data-testid={`${testId}-google`}>
            <GoogleGlyph /> Google Calendar
          </button>
          <button type="button" onClick={apple} className={`w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-left text-[13px] font-semibold transition-colors ${itemHover}`} data-testid={`${testId}-apple`}>
            <AppleGlyph /> Apple / Outlook (.ics)
          </button>
        </div>
      )}
    </div>
  );
};
